import getDisplayName from '@lib/utils/getDisplayName';
import useService from '@modules/ServiceModule/useService';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import useVoiceTask from '@modules/TasksModule/hooks/useVoiceTask';
import TasksModule from '@modules/TasksModule';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import { MicrophoneAccessType } from '@modules/BrowserModule/types';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import useTenantName from '@lib/hooks/useTenantName';
import { IncomingCallToastType } from '@modules/ToastModule/types';
import { pipLocators } from '@locators/pip';
import { getTestIdProps } from '@lib/testUtils';
import { Button, Flex } from '@servicetitan/anvil2';
import styles from '../styles/pip.module.css';

interface PipIncomingCallProps {
  readonly callToast: IncomingCallToastType;
}

export function PipIncomingCall({ callToast }: PipIncomingCallProps) {
  const { id } = callToast;
  const { userByContactCenterUserId } = useService();
  const { tasks } = useTasks();
  const { microphoneAccess } = useBrowserState();
  const {
    [FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect]:
      hasGracefullyHandleMicrophoneDisconnect,
  } = useFeatureFlags();

  const task = tasks.find(
    (task) => task.attributes?.callId === id || task.id === id,
  );

  const { isIncomingTransfer, isExternal, isInternal } = useVoiceTask(task);
  const tenantId = task?.tenantId ?? '';
  const tenantName = useTenantName(tenantId);

  if (!task) return null;

  const { displayName, from, queueName } = task;
  const workflowName = task.attributes?.workflow?.name;

  const callFrom = getDisplayName(
    displayName ?? from,
    userByContactCenterUserId,
  );

  let title = callFrom;
  if (isInternal) {
    title = `${callFrom} (Internal)`;
  } else if (isExternal && tenantName) {
    title = `${callFrom} (${tenantName})`;
  }

  const hasMicrophoneAccess =
    microphoneAccess === MicrophoneAccessType.ACCESS_GRANTED;
  const isAcceptDisabled =
    !!hasGracefullyHandleMicrophoneDisconnect && !hasMicrophoneAccess;

  const handleAccept = () => TasksModule.actionTask(task.id, 'accept');
  const handleDecline = () => TasksModule.actionTask(task.id, 'reject');

  return (
    <Flex
      direction='column'
      gap='2'
      className={`${styles.incomingCall} p-3`}
      {...getTestIdProps(pipLocators.incomingCall)}
    >
      <span className={styles.incomingCallTitle}>Incoming Call</span>
      {isIncomingTransfer && (
        <span className={styles.transferTag}>Transfer</span>
      )}
      <span className={styles.callerName}>{title}</span>
      {queueName && <span className={styles.callMeta}>Queue: {queueName}</span>}
      {workflowName && (
        <span className={styles.callMeta}>Workflow: {workflowName}</span>
      )}

      <Flex gap='2' className={styles.incomingCallActions}>
        <Button
          appearance='primary'
          onClick={handleAccept}
          disabled={isAcceptDisabled}
          data-testid={pipLocators.acceptButton}
          className={styles.acceptButton}
        >
          Accept
        </Button>
        <Button
          appearance='danger'
          onClick={handleDecline}
          data-testid={pipLocators.declineButton}
          className={styles.declineButton}
        >
          Decline
        </Button>
      </Flex>
    </Flex>
  );
}
